<template>
  <div id="bixeTips" class="bixeTips">
    <div id="microskills" class="microskills">
      <transition-group name="slideInRight" tag="span">
        <top-alerts-alert v-for="alert in visibleAlerts" :alert="alert" :key="alert.tipId" @hide="hide(alert.tipId)" class="slideInRight-item" />
      </transition-group>
    </div>
  </div>
</template>

<script setup>
import _ from 'underscore';
import { ref, computed, watch, getCurrentInstance } from 'vue';
import { storeToRefs } from 'pinia';
import TopAlertsAlert from '@/components/TopAlertsAlert';
import { useMeetingStore } from '@/store/pinia/meeting';
import { useMessagesStore } from '@/store/pinia/messages';
import { usePhaseStore } from '@/store/pinia/phase';
import { usePageStore } from '@/store/pinia/page';
import { useUsersStore } from '@/store/pinia/users';
import utils from '../resources/utils';

const $ee = getCurrentInstance().appContext.config.globalProperties.$ee;
const $meetingmanager = getCurrentInstance().appContext.config.globalProperties.$meetingmanager;

const meetingStore = useMeetingStore();
const messagesStore = useMessagesStore();
const phaseStore = usePhaseStore();
const pageStore = usePageStore();
const usersStore = useUsersStore();

const notificationsShown = ref({
  waitingAreaAlerts: new Set(),
});

const { alerts, alertsShowing: show } = storeToRefs(messagesStore);
const { initTitleCardShown } = storeToRefs(meetingStore);
const {
  me,
  iAmModerator,
  iAmSpeaking,
  iAmMuted,
  iAmSysMuted,
  firstShown: usersShown,
  inWaitingRoom,
  iAmInWaitingRoom,
  moderators,
  recommendedModerators,
  owner,
  MMNotifications,
} = storeToRefs(usersStore);
const { currentPhase, setupPhase } = storeToRefs(phaseStore);
const { current: currentPage } = storeToRefs(pageStore);

const visibleAlerts = computed(() => {
  return show.value ? [...alerts.value].reverse() : [];
});

watch(currentPhase, (nv) => {
  const isValidPhase = ['checkIn', 'checkOut'].includes(nv);
  const isValidUserState = iAmMuted.value && !iAmInWaitingRoom.value && !iAmSpeaking.value;
  const isValidUIState = initTitleCardShown.value && usersShown.value;
  if (isValidPhase && isValidUserState && isValidUIState) {
    messagesStore
      .addAlert('willUnmute', { phase: currentPhase.value })
      .then(willUnmute)
      .catch(() => {});
  } else {
    messagesStore.removeAlert({ messageName: 'willUnmute' });
  }
});
watch(iAmSpeaking, (nv) => {
  if (nv && !setupPhase.value && iAmMuted.value) {
    myTurnToSpeak();
  } else {
    messagesStore.removeAlert({ messageName: 'pleaseUnmute' });
  }
});
watch(iAmMuted, (nv) => {
  if (!nv) {
    messagesStore.removeAlert({ messageName: 'pleaseUnmute' });
    messagesStore.removeAlert({ messageName: 'willUnmute' });
  }
});
watch(iAmSysMuted, (nv) => {
  if (!nv) {
    messagesStore.removeAlert({ messageName: 'tryPushToTalk' });
  }
});
watch(inWaitingRoom, handleJoinMeetingRequestNotification, { immediate: true });
watch(moderators, notifyRemovalFromMeetingModerators, { immediate: true });
watch(recommendedModerators, getConfirmationForJoiningAsModerator, { immediate: true });
watch(MMNotifications, handleMMNotifications, { immediate: true });
watch(currentPhase, messagesStore.clearAlertsFromLayout);
watch(currentPage, messagesStore.clearAllAlerts);

function hide(tipId) {
  messagesStore.removeAlert({ tipId });
}

function myTurnToSpeak() {
  messagesStore.removeAlert({ messageName: 'willUnmute' });
  _.delay(() => {
    messagesStore
      .addAlert('pleaseUnmute', { phase: currentPhase.value })
      .then((value) => {
        if (value) {
          unmute();
        }
      })
      .catch(() => {});
  }, 300);
}

function unmute() {
  $ee.emit('nM:userButton:mute', {
    id: me.value.id,
    about: me.value.id,
    iAmModerator: iAmModerator.value,
    muted: iAmMuted.value,
    sysmuted: iAmSysMuted.value,
    unmuteInstantly: true,
  });

  messagesStore.removeAlert({ messageName: 'pleaseUnmute' });
}

function willUnmute() {
  $ee.emit('nM:userButton:mute', {
    id: me.value.id,
    about: me.value.id,
    iAmModerator: iAmModerator.value,
    muted: iAmMuted.value,
    willUnmute: true,
    sysmuted: iAmSysMuted.value,
    unmuteInstantly: iAmSpeaking.value || false,
  });
  messagesStore.removeAlert({ messageName: 'willUnmute' });
}

function handleJoinMeetingRequestNotification(requests, oldRequests) {
  if (!iAmModerator.value) {
    return;
  }
  const allUsersInWaitingRoom = requests.map(({ id }) => id);
  requests.forEach(({ id, firstName, lastName }) => {
    if (!notificationsShown.value.waitingAreaAlerts.has(id)) {
      $ee.emit('sfx', 'knock');
      messagesStore
        .addAlert('userWaiting', { firstName, lastName, userId: id })
        .then(() => {
          $meetingmanager.approveJoiningRequests({ userIds: [id] });
          messagesStore.removeAlert({ filter: (t) => t.messageName !== 'userWaiting' || t.userId !== id });
        })
        .catch(() => {
          console.log('Cancelled joining request notification for ', id);
        });
    }
  });

  notificationsShown.value.waitingAreaAlerts = new Set(allUsersInWaitingRoom);

  _.difference(oldRequests, requests).forEach(({ id }) => {
    messagesStore.removeAlert({ filter: (t) => t.messageName !== 'userWaiting' || t.userId !== id });
  });
}

function handleMMNotifications(nv, ov) {
  if (nv.recommendationsRejected?.length && nv.recommendationsRejected.length !== ov.recommendationsRejected.length) {
    showRecommendationRejectedMsg(nv, ov);
  }
}

function showRecommendationRejectedMsg(nv, ov) {
  const recentRejections = nv.recommendationsRejected.filter((userId) => !ov.recommendationsRejected.includes(userId));
  recentRejections.forEach((userId) => {
    const userName = utils.desanitizeString(usersStore.getUserById(userId).first_name);
    const type = 'recommendationAsModeratorRejected';
    getUserConfirmation({ type, userId, userName, hasActions: false })
      .catch(() => {})
      .finally(() => {
        usersStore.onRecommendationRejectedNotificationClosed({ userId });
      });
  });
}

function getUserConfirmation({ type, userId, userName, hasActions = true }) {
  return new Promise((resolve) => {
    const handleNotificationConfirmation = (doIt) => {
      resolve({ doIt });
    };
    const showBixeAlert = () => {
      messagesStore
        .addAlert(type, { userName, specialChar: '&nbsp;', id: userId })
        .then(handleNotificationConfirmation)
        .catch(() => handleNotificationConfirmation(false));
    };
    if (!utils.isOpenedWithinElectronShell()) {
      showBixeAlert();
      return;
    }
    utils.genericFns
      .showNativeNotification({ type, userId, userName, hasActions })
      .then(({ doIt, message }) => {
        if (message === 'mainWindowIsNotMinimized') {
          showBixeAlert();
        } else {
          handleNotificationConfirmation(doIt);
        }
      })
      .catch(() => {
        handleNotificationConfirmation(false);
      });
  });
}

function getConfirmationForJoiningAsModerator(recommendedModerators, oldRecommendedModerators) {
  const userId = me.value.id;
  const justRecommended = recommendedModerators.includes(userId) && (!oldRecommendedModerators || !oldRecommendedModerators.includes(userId));

  if (justRecommended) {
    const handleNotificationConfirmation = (doIt) => {
      if (doIt) {
        $meetingmanager.confirmJoiningAsMeetingController();
      } else {
        $meetingmanager.rejectMeetingControllerRecommendation();
      }
    };
    const userName = utils.desanitizeString(owner.value.first_name);
    const type = 'wouldYouLikeToBeAModerator';
    getUserConfirmation({ type, userId, userName })
      .then(({ doIt }) => {
        handleNotificationConfirmation(doIt);
      })
      .catch(() => {
        handleNotificationConfirmation(false);
      });
  }
}

function notifyRemovalFromMeetingModerators(moderators, oldModerators) {
  const userId = me.value.id;
  if (moderators.length && !moderators.includes(userId) && oldModerators && oldModerators.includes(userId)) {
    const type = 'youHaveBeenDemoted';
    getUserConfirmation({ type, userId, hasActions: false }).catch(() => {});
  }
}
</script>
<style lang="scss" scoped>
.microskills {
  position: absolute;
  z-index: $z_alerts;
  transition: 300ms ease-in-out;
  left: rem(0px);
  top: rem($top-timer-mini-height);
  width: rem(323px);
  transform-origin: rem(24px) rem(-24px);

  .presentationSpace & {
    top: 0;
  }

  & > * + * {
    margin-top: 1em;
  }
}
</style>
